import { useEffect, useState, useCallback, useRef } from "react";
import { ISystemData } from "../types/resource";
import { getAllSystemList } from "../services/systemService";

interface FetchAllSystemParams {
  currentSystemId?: any;
  setIsLoading: (loading: boolean) => void;
  enabled?: boolean;
  systemIds?: number[];
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
}

const useFetchAllSystem = ({
  currentSystemId = null,
  setIsLoading,
  enabled = true,
  systemIds,
  fetchOnMount = true, // Default to true for backward compatibility
}: FetchAllSystemParams) => {
  const [allSystemData, setAllSystemData] = useState<ISystemData[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  // Track enabled/systemIds/currentSystemId via refs to keep fetchSystems stable,
  // while still allowing re-fetch when enabled transitions from false → true.
  const enabledRef = useRef(enabled);
  const systemIdsRef = useRef(systemIds);
  const currentSystemIdRef = useRef(currentSystemId);
  enabledRef.current = enabled;
  systemIdsRef.current = systemIds;
  currentSystemIdRef.current = currentSystemId;

  const fetchSystems = useCallback(async () => {
    if (!enabledRef.current) {
      // Avoid creating a new [] reference if already empty
      setAllSystemData((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      // Use the service function with single system_id parameter
      let result: ISystemData[];
      const ids = systemIdsRef.current;
      const singleId = currentSystemIdRef.current;
      if (ids && ids.length > 0) {
        // For now, just take the first system ID since service only supports single ID
        result = await getAllSystemList(ids[0]);
      } else if (singleId) {
        result = await getAllSystemList(singleId);
      } else {
        result = await getAllSystemList();
      }

      setAllSystemData(Array.isArray(result) ? result : []);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching systems:", err);
      setAllSystemData([]);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  // Re-trigger fetch when enabled transitions to true (e.g. after systemIds become available)
  useEffect(() => {
    if (fetchOnMount && enabled) {
      fetchSystems();
    }
  }, [fetchOnMount, enabled, fetchSystems]);

  return [allSystemData, fetchSystems, isFetching] as const;
};

export default useFetchAllSystem;
